<script lang="ts" setup>
import { ref, onBeforeMount, onMounted } from 'vue'
import ImageSwiper from '@/components/ImageSwiper.vue'
import type ImageSwiperType from '@/components/ImageSwiper.vue'
import Buttons from '@/components/GalleryButtons.vue'
import ImageThumbnails from '@/components/ImageThumbnails.vue'
import GallerySvgSprite from '@/components/GallerySvgSprite.vue'
import { useGalleryState } from '@/composables/useGalleryState'
import { useGalleryHelpers } from '@/composables/useGalleryHelpers'
import { watchOnce } from '@vueuse/core'
import { useGalleryFactory } from '@/model/GalleryFactory'
import GalleryInfo from '@/components/GalleryInfo.vue'
import { useNavigationPropagation } from '@/composables/useNavigationPropagation'
import '@/assets/styles/main.scss'
import ImageLightbox from '@/components/ImageLightbox.vue'
import { userAdvertHelper } from '@/composables/userAdvertHelper'
import { useLanguage } from '@/composables/useLanguage'

const { gallery, setGallery, loaded, showThumbnails, activeSlide, isNavigationReloadForced } = useGalleryState()
const { updateBrowserUrl, resolveActiveSlide } = useGalleryHelpers()
const { dispatchEventNavigationChanged, dispatchEventNavigationSource } = useNavigationPropagation()
const { adSlotClass, dispatchEventAdvertSlotReady } = userAdvertHelper()
const { createFromConfig } = useGalleryFactory()
const imageSwiperRef = ref<InstanceType<typeof ImageSwiperType> | null>(null)
const { detectLanguage } = useLanguage()
detectLanguage()

const init = () => {
  setGallery(createFromConfig(window.anzu.gallery))
}

const backToSwiper = (index: number) => {
  if (index) index--
  showThumbnails.value = false
  activeSlide.value = index
  if (imageSwiperRef.value) imageSwiperRef.value.slideTo(index)
  updateBrowserUrl(activeSlide.value)
}

watchOnce(loaded, (newValue) => {
  if (newValue) {
    const newIndex = resolveActiveSlide()
    if (newIndex > 0) {
      window.setTimeout(() => {
        backToSwiper(newIndex)
      }, 50)
    }
  }
})

const onSlideChange = () => {
  updateBrowserUrl(activeSlide.value)
  dispatchEventNavigationChanged()
}

const onInteraction = (interactionSource: string) => {
  dispatchEventNavigationSource(interactionSource)
}

const slidePrev = () => {
  // @ts-ignore
  if (isNavigationReloadForced(imageSwiperRef.value ?? null)) return

  if (imageSwiperRef.value) imageSwiperRef.value.slidePrev()
}

const slideNext = () => {
  // @ts-ignore
  if (isNavigationReloadForced(imageSwiperRef.value ?? null)) return

  if (imageSwiperRef.value) imageSwiperRef.value.slideNext()
}

const lightboxVisibleRef = ref(false)
const lightboxHide = () => {
  lightboxVisibleRef.value = false
}

const zoomIn = () => {
  lightboxVisibleRef.value = true
}

onBeforeMount(() => {
  init()
})
onMounted(() => {
  dispatchEventAdvertSlotReady()
})
</script>

<template>
  <div class="page-wrapper">
    <header class="page-header">
      <div class="left-side">
        <a v-if="gallery.config.headLogoName" class="logo" href="/" title="SME {{ gallery.config.headLogoName }}">
          <svg aria-label="SME logo" class="svg-icon" viewBox="0 0 138 48">
            <use xlink:href="#svg-icon-sme-logo"></use>
          </svg>
          <span>{{ gallery.config.headLogoName }}</span>
        </a>
        <h2 class="gallery-title">{{ gallery.texts.title }}</h2>
      </div>
      <div class="right-side">
        <buttons
          @slideNext="slideNext"
          @slidePrev="slidePrev"
          @onInteraction="onInteraction"
          @zoomIn="zoomIn"
        ></buttons>
      </div>
    </header>
    <div v-if="loaded" class="content">
      <aside>
        <gallery-info></gallery-info>
        <div :class="adSlotClass"></div>
      </aside>
      <main>
        <image-swiper
          v-show="!showThumbnails"
          ref="imageSwiperRef"
          @slideChange="onSlideChange"
          @onInteraction="onInteraction"
        ></image-swiper>
        <image-thumbnails v-show="showThumbnails" @backToSwiper="backToSwiper"></image-thumbnails>
      </main>
    </div>
    <div v-else class="loader"></div>
    <gallery-svg-sprite></gallery-svg-sprite>
    <image-lightbox :visible="lightboxVisibleRef" @hide="lightboxHide"></image-lightbox>
  </div>
</template>
